import { Avatar, Chip, Tooltip, Grid } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { emitCustomEvent } from 'react-custom-events';
import { useDispatch, useSelector } from 'react-redux';
import ColorConstants from '../../../defaults/ColorConstants';
import Events from '../../../defaults/Events';
import FieldNames from '../../../defaults/FieldNames.json';
import PolygonIcon from '../../../icons/PolygonIcon';
import {
    setActiveDrawTool,
    setFilterGeometry,
    setIsGroupingOnly,
} from '../../../redux/slices/filterSlice';
import ErrorService from '../../../services/ErrorService';
import FeatureService from '../../../services/FeatureService';
import GroupingService from '../../../services/GroupingService';
import { getFeatureGroupItemsOrganized } from '../../../utils/GroupingHelper';
import Grouping from '../../grouping/Grouping';
import _ from 'lodash';
import {sortFeatureAttributesByDate} from "../../../utils/DateHelper";
import {createFeatureObject} from "../../../utils/FeatureHelper";
import {setListCheckedFeatures} from "../../../redux/slices/displayStateSlice";

export default function AssociateChips({
    detailIdentifier,
    editableFeature,
    setAssociatedCount,
    isNewFeature, // NOTE: Leave unchanged at this time - prop is incorrectly renamed during drill in this instance, but correctly in another. Changing it introduces a whitescreen with untraceable bug
    isLoading,
    setIsLoading,
    isCurrentActual,
    associatedCount,
    setEditableFeature,
    featureGroups,
    setFeatureGroups,
}) {
    const dispatch = useDispatch();

    const treatments = useSelector((state) => state.treatments.visibleTreatments);
    const activities = useSelector((state) => state.activities.visibleActivities);
    const filterGeometry = useSelector((state) => state.filter.filterGeometry);
    const actualTreatments = useSelector(
        (state) => state.actualsTreatments.visibleActualsTreatments
    );
    const actualActivities = useSelector(
        (state) => state.actualsActivities.visibleActualsActivities
    );
    const config = useSelector((state) => state.configuration.config);
    const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
    const nifcToken = useSelector((state) => state.configuration.nifcToken);
    const activeDrawTool = useSelector((state) => state.filter.activeDrawTool);
    const feature = useSelector((state) => state.details.feature);
    const isTreatment = useSelector((state) => state.details.isTreatment);

    const [associateOpen, setAssociateOpen] = useState(false);
    const [featureGroupItems, setFeatureGroupItems] = useState([]);
    const [groupingsOpen, setGroupingsOpen] = useState(false);
    const [featureGroupIds, setFeatureGroupIds] = useState([]);

    useEffect(() => {
        /* Clear list on unmount to prevent leakage between separate
        lists that use this common component and state value */
        return () => dispatch(setListCheckedFeatures([]));
    }, [])

    useEffect(() => {
        if (
            feature?.attributes &&
            feature.attributes[FieldNames.featureFields.objectID] &&
            !isNewFeature
        ) {
            let geoType =
                feature?.attributes?.[FieldNames.featureFields.joinFeature] ??
                FieldNames.joinFeatureValues.polygon;
            GroupingService.getAllGroupItemsForFeatures(
                [feature.attributes[FieldNames.featureFields.joinID]],
                geoType
            )
                .then((response) => {
                    if (response && response.GroupItems && response.GroupItems.length > 0) {
                        const groups = response.Groups;
                        if (groups && groups.length > 0) {
                            setFeatureGroupIds(groups);
                        }
                    }
                    setAssociatedCount(response.GroupItems.length);
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });
        }
    }, [feature, isNewFeature, config, rootServiceUrl, setAssociatedCount]);

    const associateHandleClose = (isSaved) => {
        setAssociateOpen(false);
        dispatch(setListCheckedFeatures([]));
        dispatch(setFilterGeometry(null));
        dispatch(setActiveDrawTool(null));
        dispatch(setIsGroupingOnly(false));
    };

    const drawFilterCompleted = (geometry) => {
        dispatch(setFilterGeometry(geometry));
        dispatch(setIsGroupingOnly(true));
        setIsLoading(true);
        dispatch(setActiveDrawTool(null));
        setAssociateOpen(true);
    };

    const cancelSelectedTool = () => {
        dispatch(setActiveDrawTool(null));
        emitCustomEvent(Events.DisableDrawMode);
        dispatch(setIsGroupingOnly(false));
    };

    const polygonToolSelected = () => {
        if (activeDrawTool === 'polygon') {
            cancelSelectedTool();
        }
        dispatch(setActiveDrawTool('polygon'));
            dispatch(setListCheckedFeatures([]));

        emitCustomEvent(Events.EnableDrawMode, { geometry: null, callback: drawFilterCompleted });
    };


    useEffect(() => {
        if (associateOpen && filterGeometry) {
            let tempFeatures = [];
            let allFeatures = treatments
                .concat(activities)
                .concat(actualTreatments)
                .concat(actualActivities);
            let allFeaturesFlattened = [];
            if (allFeaturesFlattened?.length > 0) {
                allFeaturesFlattened = allFeatures?.flatten();
            }
            allFeaturesFlattened?.forEach((feature) => {
                let featureAttr = feature;
                if (feature.attributes) {
                    featureAttr = feature.attributes;
                }
                const obj = createFeatureObject(featureAttr, null);
                tempFeatures.push(obj);
            });

            let currentObj;
            let featureAttr = editableFeature?.attributes;
            currentObj = createFeatureObject(featureAttr, null);

            let tempEditableFeature = tempFeatures.filter((x) => x.ID === currentObj.ID);
            if (tempEditableFeature.length === 0) {
                tempFeatures.push(currentObj);
            }

            sortFeatureAttributesByDate(
                tempFeatures,
                FieldNames.featureFields.initiationDate,
                false
            );

            dispatch(setListCheckedFeatures(tempFeatures));
        }
    }, [
        treatments,
        activities,
        associateOpen,
        editableFeature,
        filterGeometry,
        actualTreatments,
        actualActivities,
        isTreatment,
        isCurrentActual,
    ]);

    const onGroupEdit = () => {
        setFeatureGroupIds([]);
        setFeatureGroups([]);
        //Need this here so that the list is cleared before adding more
        setFeatureGroupItems([]);
        let tempEditableFeature = _.cloneDeep(editableFeature);
        tempEditableFeature.geometry = editableFeature.geometry;

        if (isTreatment !== null) {
            let geoType =
                tempEditableFeature?.attributes?.[FieldNames.featureFields.joinFeature] ??
                FieldNames.joinFeatureValues.polygon;
            GroupingService.getAllGroupItemsForFeatures(
                [tempEditableFeature.attributes[FieldNames.featureFields.joinID]],
                geoType
            )
                .then((currentFeatureGroupItems) => {
                    //currentFeatureGroupItems is all groups and group items for the treatment/activity we are on
                    if (
                        currentFeatureGroupItems &&
                        currentFeatureGroupItems.GroupItems &&
                        currentFeatureGroupItems.GroupItems.length > 0
                    ) {
                        const groups = currentFeatureGroupItems.Groups;
                        if (groups && groups.length > 0) {
                            const allGroupIds = groups.map(
                                (ea) => ea[FieldNames.treatmentActivityGroups.id]
                            );
                            setFeatureGroupIds(allGroupIds);
                            setFeatureGroups(groups);
                            tempEditableFeature.attributes[FieldNames.featureFields.hasGroup] =
                                FieldNames.yesNo.yes;
                            tempEditableFeature.attributes[FieldNames.featureFields.groupCount] =
                                allGroupIds.length;

                            FeatureService.updateFeatureAttributes([tempEditableFeature.attributes])
                                .then((resp) => {
                                    if (
                                        resp &&
                                        resp.updateResults &&
                                        resp.updateResults.length > 0 &&
                                        resp.updateResults[0].success
                                    ) {
                                        setEditableFeature(tempEditableFeature);

                                        GroupingService.getAllGroupItemsByGroupIds(
                                            allGroupIds
                                        ).then((items) => {
                                            //All Group Items in the groups that this treatment/activity has access to
                                            if (items && items.length > 0) {
                                                getFeatureGroupItemsOrganized(
                                                    items,
                                                    setFeatureGroupItems,
                                                    rootServiceUrl,
                                                    config,
                                                    nifcToken
                                                );
                                                setGroupingsOpen(true);
                                            }
                                        });
                                    }
                                })
                                .catch((error) => {
                                    ErrorService.addErrorLogEntry(
                                        error
                                    );
                                });
                        }
                    } else {
                        tempEditableFeature.attributes[FieldNames.featureFields.hasGroup] =
                            FieldNames.yesNo.no;
                        tempEditableFeature.attributes[FieldNames.featureFields.groupCount] = 0;
                        FeatureService.updateFeatureAttributes([tempEditableFeature.attributes])
                            .then((resp) => {
                                if (
                                    resp &&
                                    resp.updateResults &&
                                    resp.updateResults.length > 0 &&
                                    resp.updateResults[0].success
                                ) {
                                    setEditableFeature(tempEditableFeature);
                                }
                            })
                            .catch((error) => {
                                ErrorService.addErrorLogEntry(
                                    error
                                );
                            });
                    }
                })
                .catch((error) => {
                    ErrorService.addErrorLogEntry(
                        error
                    );
                });
        }
    };

    if (detailIdentifier === 'proposalPanel') {
        return (
            <Fragment>
                <Grid
                    item
                    size={12}
                    container
                    direction='row'
                    className='grid-item'
                    style={{ alignItems: 'center', paddingRight: '5px' }}
                >
                    {feature &&
                        feature.attributes &&
                        feature.attributes[FieldNames.featureFields.hasGroup] ===
                            FieldNames.yesNo.yes &&
                        feature.attributes[FieldNames.featureFields.groupCount] > 0 && (
                            <Grid item size={3}>
                                <Tooltip title={isNewFeature ? 'Save first to Manage Groups' : ''}>
                                    <span>
                                        <Chip
                                            className='required-field-status-chip'
                                            onClick={() => onGroupEdit()}
                                            disabled={isNewFeature}
                                            style={{
                                                backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                                marginBottom: 5,
                                                marginTop: 5,
                                            }}
                                            avatar={
                                                <Avatar
                                                    style={{
                                                        fontSize: '12px',
                                                        width: '22px',
                                                        height: '22px',
                                                        backgroundColor: ColorConstants.AvatarColor,
                                                        color: ColorConstants.WhiteAlien,
                                                    }}
                                                >
                                                    {
                                                        feature.attributes[
                                                            FieldNames.featureFields.groupCount
                                                        ]
                                                    }
                                                </Avatar>
                                            }
                                            label={
                                                <span
                                                    style={{ fontSize: '12px', lineHeight: '21px' }}
                                                >
                                                    {feature.attributes[
                                                        FieldNames.featureFields.groupCount
                                                    ] === 1
                                                        ? 'Associated Group'
                                                        : 'Associated Groups'}
                                                </span>
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            </Grid>
                        )}
                    <Grid item size={3}>
                        <Tooltip title={isNewFeature ? 'Save first to Associate' : ''}>
                            <span>
                                <Chip
                                    className='required-field-status-chip'
                                    onClick={() => polygonToolSelected()}
                                    disabled={isNewFeature}
                                    style={{
                                        backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                                        marginBottom: 5,
                                        marginTop: 5,
                                    }}
                                    avatar={
                                        <Avatar
                                            style={{
                                                fontSize: '12px',
                                                width: '42px',
                                                height: '28px',
                                                backgroundColor: 'transparent',
                                                color: ColorConstants.AvatarColor,
                                            }}
                                        >
                                            <PolygonIcon />
                                        </Avatar>
                                    }
                                    label={
                                        <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                            Associate
                                        </span>
                                    }
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                {(associateOpen || groupingsOpen) && (
                    <Grouping
                        associateOpen={associateOpen}
                        groupingsOpen={groupingsOpen}
                        setGroupingsOpen={setGroupingsOpen}
                        featureGroupIds={featureGroupIds}
                        setFeatureGroupIds={setFeatureGroupIds}
                        featureGroupItems={featureGroupItems}
                        setFeatureGroupItems={setFeatureGroupItems}
                        associateHandleClose={associateHandleClose}
                        associatedCount={associatedCount}
                        setAssociatedCount={setAssociatedCount}
                        isLoading={isLoading}
                        treatments={treatments}
                        activities={activities}
                        currentFeature={editableFeature}
                        setCurrentFeature={setEditableFeature}
                        featureGroups={featureGroups}
                        actualTreatments={actualTreatments}
                        actualActivities={actualActivities}
                        setIsLoading={setIsLoading}
                        isFromList={false}
                    />
                )}
            </Fragment>
        );
    } else {
        return '';
    }
}
